<template>
  <div class="has-tab-bar">
    <AppBar
      title="Locaties"
      :show-back-button="false"
      :show-profile-button="true"
    />
    <spinner-loader v-if="loading" />
    <div class="overview mx-auto px-5 pb-8 md:px-6 md:pb-16">
      <h1 class="mt-7 mb-4 md:mt-9 md:mb-5 md:text-3xl">Huur een baan</h1>

      <div class="filters flex flex-wrap items-center gap-2 md:gap-3">
        <dropdown
          :selected="selectedCityKey"
          :disabled="false"
          :items="cityOptions"
          empty-option="Alle steden"
          theme="rounded"
          class="city-select"
          @on-select="(val) => selectCity(val)"
        />
        <div
          v-if="listOfActivities.length > 0"
          class="sport-chips flex gap-2"
          role="group"
          aria-label="Sport"
        >
          <button
            v-for="chip in sportChips"
            :key="chip.key || 'all'"
            type="button"
            class="sport-chip"
            :class="{ 'sport-chip-active': chip.active }"
            :aria-pressed="chip.active ? 'true' : 'false'"
            @click="selectActivity(chip.key)"
          >
            {{ chip.label }}
          </button>
        </div>
        <div class="filters-spacer hidden md:block" />
        <div
          class="sort-toggle flex flex-shrink-0 h-11 border border-gray-400 rounded-full p-0.5"
          role="group"
          aria-label="Sorteren"
        >
          <button
            type="button"
            class="sort-option sort-near"
            :class="{ 'sort-option-active': activeSort === 'near' }"
            :aria-pressed="activeSort === 'near' ? 'true' : 'false'"
            @click="sortNear"
          >
            Dichtbij
          </button>
          <button
            type="button"
            class="sort-option sort-popular"
            :class="{ 'sort-option-active': activeSort === 'popular' }"
            :aria-pressed="activeSort === 'popular' ? 'true' : 'false'"
            @click="sortPopular"
          >
            Populair
          </button>
        </div>
      </div>

      <div
        v-if="showOrigin"
        class="origin-line mt-3.5 flex items-center gap-1.5 text-sm text-gray-600"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#047857"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
        <span class="origin-label">Afstand vanaf {{ origin.label }}</span>
        <span aria-hidden="true">·</span>
        <button
          type="button"
          class="change-origin h-8 px-1 text-sm font-medium text-green-700 underline"
          @click="editOrigin"
        >
          wijzig
        </button>
      </div>
      <p v-if="locating" class="mt-3.5 mb-0 text-sm text-gray-600">
        Je locatie wordt bepaald…
      </p>
      <div
        v-if="showPostcodePanel"
        class="postcode-panel mt-3.5 p-3.5 rounded-lg bg-gray-100 flex flex-col gap-2.5 md:max-w-lg md:p-4"
      >
        <p v-if="geoMessage" class="geo-message m-0 text-sm text-gray-600">
          {{ geoMessage }}
        </p>
        <label for="locations-postcode" class="text-sm font-medium">
          Postcode of plaats
        </label>
        <form class="flex gap-2" @submit.prevent="usePostcode">
          <input
            id="locations-postcode"
            v-model="postcodeDraft"
            type="text"
            autocomplete="postal-code"
            placeholder="1234 AB"
            class="flex-1 min-w-0 h-11 px-3 border border-gray-400 rounded-lg bg-white text-sm"
          />
          <button
            type="submit"
            class="use-postcode flex-shrink-0 h-11 px-4 rounded-xl bg-green-600 text-white text-sm font-medium disabled:opacity-50"
            :disabled="geocoding || !postcodeDraft.trim()"
          >
            Gebruik
          </button>
        </form>
        <p v-if="postcodeError" class="postcode-error m-0 text-sm text-red-600">
          {{ postcodeError }}
        </p>
        <button
          v-if="canOfferBrowser"
          type="button"
          class="use-browser self-start h-8 inline-flex items-center gap-1.5 text-sm font-medium text-green-700"
          @click="requestBrowserLocation"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="7" />
            <circle cx="12" cy="12" r="2" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
          </svg>
          Gebruik mijn huidige locatie
        </button>
      </div>

      <p
        v-if="!loading"
        class="result-count mt-4 mb-2 text-sm text-gray-600 md:mt-5 md:mb-3"
      >
        {{ countLabel }}
      </p>

      <div v-if="sortedList.length" class="location-grid">
        <router-link
          v-for="location in sortedList"
          :key="location.id"
          :to="locationLink(location)"
          class="location-card no-underline text-gray-800 hover:text-gray-800"
        >
          <img
            class="location-card-photo object-cover bg-gray-200"
            :src="location.imageUrl || noImage"
            :alt="`Foto van ${location.name}`"
            loading="lazy"
            @error="onImageError"
          />
          <div class="location-card-body flex-1 min-w-0 flex flex-col">
            <div class="flex items-baseline gap-2">
              <span
                class="location-card-name flex-1 min-w-0 truncate font-semibold"
              >
                {{ location.name }}
              </span>
              <span
                v-if="isNear && distanceLabels[location.id]"
                class="location-card-distance flex-shrink-0 text-gray-600"
              >
                {{ distanceLabels[location.id] }}
              </span>
            </div>
            <span class="location-card-meta text-gray-600">
              {{ locationMeta(location) }}
            </span>
            <div class="location-card-footer">
              <span v-if="hasPrice(location)" class="location-card-price">
                v.a.
                <strong class="font-semibold">
                  {{ $n(location.lowestNonMemberPrice.amount, 'currency') }}
                </strong>
                / {{ location.lowestNonMemberPrice.duration }} min
                <span v-if="hasMemberPrice(location)" class="text-gray-600">
                  · leden
                  {{ $n(location.lowestMemberPrice.amount, 'currency') }}
                </span>
              </span>
              <span
                v-if="location.creditcards && location.creditcards.length"
                class="card-label"
              >
                Strippenkaart
              </span>
            </div>
          </div>
        </router-link>
      </div>

      <div
        v-else-if="!loading"
        class="empty-state mt-6 py-7 px-5 border border-dashed border-gray-300 rounded-xl text-center md:py-12 md:px-6"
      >
        <p class="empty-title m-0 font-semibold">{{ emptyTitle }}</p>
        <p class="mt-1.5 mb-4 text-sm text-gray-600">
          Probeer een andere stad of sport.
        </p>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="clear-filters h-10 px-5 border border-green-600 rounded-2xl bg-white text-green-700 text-sm font-medium"
          @click="clearFilters"
        >
          Filters wissen
        </button>
      </div>
    </div>
    <TabBar selected="locations" />
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import TabBar from '@/components/TabBar'
import { mapState, mapGetters } from 'vuex'
import { LOCATION_GET_COMMERCIAL } from '@/store/actions/location'
import api, { googleSearchLongAndLatOnMap } from '@/utils/api'
import { ACTIVITY_LIST } from '@/store/actions/activity'
import { PARTICIPANT_OWN } from '@/store/actions/participant'
import Dropdown from '@/components/Dropdown'
import SpinnerLoader from '../../components/SpinnerLoader.vue'
import noImage from '@/assets/images/img_no-image.png'
import {
  toLatLng,
  distanceKm,
  formatDistance,
  sortByDistance
} from '@/utils/distance'

// #281 storage: localStorage holds only the sort choice and a postcode/plaats
// the user typed (functional, outside the cookie consent of consent.js). The
// browser position lives in memory and sessionStorage only. Nothing is sent to
// the API and the profile is never changed.
export const SORT_KEY = 'locationsOverview.sort'
export const POSTCODE_KEY = 'locationsOverview.postcode'
export const BROWSER_POSITION_KEY = 'locationsOverview.browserPosition'
export const POSTCODE_ORIGIN_KEY = 'locationsOverview.postcodeOrigin'

const SORT_NEAR = 'near'
// a permission prompt that is never answered calls neither callback of
// getCurrentPosition (its timeout only starts after the grant): after this
// long the postcode field shows anyway
export const GEOLOCATION_WATCHDOG_MS = 12000
// most bookings in the last 12 months first (API, cached there for an hour);
// replaced the A–Z sort, whose stored value 'az' still means this
const SORT_POPULAR = 'popular'
const LEGACY_SORT_AZ = 'az'

function readStorage(storage, key) {
  try {
    return window[storage].getItem(key)
  } catch (e) {
    return null
  }
}

function writeStorage(storage, key, value) {
  try {
    window[storage].setItem(key, value)
  } catch (e) {
    // private mode or blocked storage: the choice just isn't remembered
  }
}

function readJson(storage, key) {
  try {
    return JSON.parse(readStorage(storage, key))
  } catch (e) {
    return null
  }
}

function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

export default {
  name: 'LocationsOverview',
  components: {
    AppBar,
    Dropdown,
    TabBar,
    SpinnerLoader
  },
  data() {
    return {
      noImage,
      // { [locationId]: bookings in the last 12 months }
      popularity: {},
      selectedCity: undefined,
      selectedActivity: undefined,
      cityQuery: this.$route.query.c,
      basePath: this.$route.path.substring(
        0,
        this.$route.path.indexOf('/', 1) > 0
          ? this.$route.path.indexOf('/', 1)
          : this.$route.path.length
      ),
      // the stored sort choice; null means "the default for this visitor"
      sortChoice: null,
      // the user tapped Dichtbij during this visit and has no origin yet
      nearRequested: false,
      // 'browser' or 'postcode' once the user picked that origin
      originSource: null,
      browserPosition: null,
      postcodeOrigin: null,
      postcodeDraft: '',
      postcodeError: null,
      editingOrigin: false,
      locating: false,
      // bumped per position request; an answer to an older (or cancelled)
      // request is ignored
      geoRequest: 0,
      geoWatchdog: null,
      geocoding: false,
      // bumped per geocode and when the browser position wins; an older
      // geocode answer then no longer overrides the latest origin choice
      geocodeRequest: 0,
      // null, 'denied' (permission refused), 'unavailable' (no geolocation)
      // or 'failed' (timeout / position unavailable)
      geoState: null
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated']),
    ...mapState({
      status: (state) => state.location.status,
      locations: (state) => state.location.commercial,
      activities: (state) => state.activity.activityList,
      profile: (state) => state.participant && state.participant.ownProfile
    }),
    listOfCities: function () {
      // sort alphabetically
      return this.locations
        .reduce(
          (list, location) => [
            ...list,
            ...(!list.find(
              (item) => item.key === location.address.city.trim().toLowerCase()
            ) && location.address.city
              ? [
                  {
                    key: location.address.city.toLowerCase(),
                    value:
                      location.address.city.charAt(0).toUpperCase() +
                      location.address.city.slice(1).toLowerCase()
                  }
                ]
              : [])
          ],
          []
        )
        .sort((a, b) => a.value.localeCompare(b.value))
    },
    selectedCityKey: function () {
      return this.selectedCity ? this.selectedCity.trim().toLowerCase() : ''
    },
    // a city from ?c= without locations is still shown as the active filter
    cityOptions: function () {
      const key = this.selectedCityKey
      if (!key || this.listOfCities.find((item) => item.key === key)) {
        return this.listOfCities
      }
      return [...this.listOfCities, { key, value: capitalize(key) }]
    },
    listOfActivities: function () {
      return (
        this.locations.reduce((list, location) => {
          location.activities?.forEach((activity) => {
            if (list.length === 0 || !list.find((l) => l.id === activity)) {
              const act = this.activities.find((a) => a.id === activity)
              if (act) {
                list.push({
                  id: act.id,
                  // fall back to the id when an activity has no slug
                  key: act.slug || String(act.id),
                  value: act.name
                })
              }
            }
          })
          return list.sort((a, b) => a.value.localeCompare(b.value))
        }, []) || []
      )
    },
    sportChips: function () {
      return [
        { key: '', label: 'Alle sporten', active: !this.selectedActivity },
        ...this.listOfActivities.map((activity) => ({
          key: activity.key,
          label: activity.value,
          active: activity.key === this.selectedActivity
        }))
      ]
    },
    filteredList: function () {
      // the chips emit the activity slug; locations list activity ids
      const activityId = this.listOfActivities.find(
        (activity) => activity.key === this.selectedActivity
      )?.id
      return this.locations.filter(
        (location) =>
          (this.selectedCity
            ? location.address.city.trim().toLowerCase() ===
              this.selectedCity.toLowerCase()
            : true) &&
          (this.selectedActivity
            ? !!activityId && location.activities?.includes(activityId)
            : true)
      )
    },
    profileCoordinates: function () {
      return (
        (this.isAuthenticated && toLatLng(this.profile?.coordinates)) || null
      )
    },
    // where the distance is measured from: a choice made on this page first
    // (browser position, typed postcode), else the profile's coordinates
    origin: function () {
      if (this.originSource === 'browser' && this.browserPosition) {
        return { coords: this.browserPosition, label: 'je huidige locatie' }
      }
      if (this.originSource === 'postcode' && this.postcodeOrigin) {
        return {
          coords: this.postcodeOrigin,
          label: this.postcodeOrigin.query
        }
      }
      if (this.profileCoordinates) {
        return {
          coords: this.profileCoordinates,
          label: this.profile.city ? this.profile.city : 'je woonplaats'
        }
      }
      return null
    },
    sort: function () {
      return (
        this.sortChoice || (this.profileCoordinates ? SORT_NEAR : SORT_POPULAR)
      )
    },
    // sorted by distance only when there is an origin to measure from
    isNear: function () {
      return this.sort === SORT_NEAR && !!this.origin
    },
    activeSort: function () {
      return this.isNear || this.nearRequested ? SORT_NEAR : SORT_POPULAR
    },
    distances: function () {
      if (!this.origin) return {}
      return this.locations.reduce(
        (map, location) => ({
          ...map,
          [location.id]: distanceKm(this.origin.coords, location.coordinates)
        }),
        {}
      )
    },
    distanceLabels: function () {
      return Object.keys(this.distances).reduce(
        (map, id) => ({ ...map, [id]: formatDistance(this.distances[id]) }),
        {}
      )
    },
    sortedList: function () {
      const alphabetical = [...this.filteredList].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'nl', {
          sensitivity: 'base'
        })
      )
      if (!this.isNear) {
        // without counts (not loaded or failed) this stays alphabetical
        const bookings = (location) => this.popularity[location.id] || 0
        return alphabetical.sort((a, b) => bookings(b) - bookings(a))
      }
      return sortByDistance(
        alphabetical,
        (location) => this.distances[location.id]
      )
    },
    showOrigin: function () {
      return this.isNear && !this.editingOrigin
    },
    showPostcodePanel: function () {
      return (
        !this.locating &&
        (this.editingOrigin || (this.nearRequested && !this.origin))
      )
    },
    hasGeolocation: function () {
      return typeof navigator !== 'undefined' && !!navigator.geolocation
    },
    canOfferBrowser: function () {
      return (
        this.hasGeolocation &&
        this.geoState !== 'denied' &&
        this.geoState !== 'unavailable'
      )
    },
    geoMessage: function () {
      if (this.geoState === 'denied' || this.geoState === 'unavailable') {
        return 'Locatie delen staat uit. Vul een postcode of plaats in om op afstand te sorteren.'
      }
      if (this.geoState === 'failed') {
        return 'We konden je locatie niet bepalen. Vul een postcode of plaats in om op afstand te sorteren.'
      }
      return null
    },
    countLabel: function () {
      const count = this.sortedList.length
      return count === 1 ? '1 locatie' : `${count} locaties`
    },
    hasActiveFilters: function () {
      return !!(this.selectedCity || this.selectedActivity)
    },
    selectedCityName: function () {
      if (!this.selectedCity) return null
      const city = this.listOfCities.find(
        (item) => item.key === this.selectedCity.toLowerCase()
      )
      return city ? city.value : capitalize(this.selectedCity)
    },
    selectedActivityName: function () {
      if (!this.selectedActivity) return null
      const activity = this.listOfActivities.find(
        (item) => item.key === this.selectedActivity
      )
      return (activity ? activity.value : this.selectedActivity).toLowerCase()
    },
    // "Geen locaties voor padel in Ede", or only the sport or the city
    emptyTitle: function () {
      const sport = this.selectedActivityName
      const city = this.selectedCityName
      if (!sport && !city) return 'Geen locaties gevonden'
      return [
        'Geen locaties',
        ...(sport ? [`voor ${sport}`] : []),
        ...(city ? [`in ${city}`] : [])
      ].join(' ')
    },
    loading: function () {
      return this.status === 'loading'
    }
  },
  metaInfo() {
    return {
      title: `Boek een locatie ${
        this.selectedCity
          ? `in ${
              this.selectedCity.charAt(0).toUpperCase() +
              this.selectedCity.slice(1)
            }`
          : ''
      } | Inviplay`
    }
  },
  watch: {
    '$route.query.c'(q) {
      this.cityQuery = q
      this.selectedCity = q
    }
  },
  beforeDestroy() {
    this.cancelBrowserLocation()
  },
  created: function () {
    this.$store.dispatch(LOCATION_GET_COMMERCIAL)
    this.$store.dispatch(ACTIVITY_LIST)
    this.loadPopularity()
    if (this.isAuthenticated && !this.profile?.id) {
      this.$store.dispatch(PARTICIPANT_OWN)
    }
    if (this.cityQuery) {
      this.selectedCity = this.cityQuery
    }
    this.restoreSortAndOrigin()
  },
  methods: {
    // never calls navigator.geolocation: a browser position only comes back
    // from sessionStorage (asked earlier in this session)
    restoreSortAndOrigin() {
      const sort = readStorage('localStorage', SORT_KEY)
      this.sortChoice =
        sort === SORT_NEAR
          ? SORT_NEAR
          : sort === SORT_POPULAR || sort === LEGACY_SORT_AZ
          ? SORT_POPULAR
          : null

      const position = toLatLng(
        readJson('sessionStorage', BROWSER_POSITION_KEY)
      )
      if (position) {
        this.browserPosition = position
        this.originSource = 'browser'
        return
      }
      const postcode = readStorage('localStorage', POSTCODE_KEY)
      if (!postcode) return
      this.postcodeDraft = postcode
      const cached = readJson('sessionStorage', POSTCODE_ORIGIN_KEY)
      const coords = cached && cached.query === postcode && toLatLng(cached)
      if (coords) {
        this.postcodeOrigin = { query: postcode, ...coords }
        this.originSource = 'postcode'
      } else if (this.sortChoice === SORT_NEAR) {
        // geocode again only when the distance is actually wanted
        this.geocodePostcode(postcode)
      }
    },
    locationLink(location) {
      const c = this.$route.query.c
      return {
        path: `${this.basePath}/${location.slug}`,
        query: c ? { c } : {}
      }
    },
    locationMeta(location) {
      const city = location.address?.city
        ? capitalize(location.address.city.trim())
        : ''
      const sports = (location.activities || [])
        .map((id) => this.activities.find((a) => a.id === id)?.name)
        .filter(Boolean)
        .join(', ')
      return [city, sports].filter(Boolean).join(' · ')
    },
    hasPrice(location) {
      return !!(
        location.lowestNonMemberPrice &&
        location.lowestNonMemberPrice.amount !== undefined
      )
    },
    hasMemberPrice(location) {
      return !!(
        location.hasMemberPrice &&
        location.lowestMemberPrice &&
        location.lowestMemberPrice.amount !== undefined
      )
    },
    onImageError(event) {
      if (!event.target.src.endsWith(noImage)) {
        event.target.src = noImage
      }
    },
    selectCity(city) {
      const value = city || ''
      // the select reports the lowercase key, also for ?c=Utrecht
      if (value.toLowerCase() === (this.$route.query.c || '').toLowerCase()) {
        this.selectedCity = value || undefined
        return
      }
      const query = { ...this.$route.query }
      if (value) {
        query.c = value
      } else {
        delete query.c
      }
      this.$router.push({ path: this.$route.path, query })
    },
    selectActivity(val) {
      this.selectedActivity = val || undefined
    },
    clearFilters() {
      this.selectedActivity = undefined
      this.selectCity('')
      this.selectedCity = undefined
    },
    setSort(sort) {
      this.sortChoice = sort
      writeStorage('localStorage', SORT_KEY, sort)
    },
    async loadPopularity() {
      try {
        const resp = await api.get('commercial_location/popularity')
        this.popularity = resp?.data || {}
      } catch (e) {
        this.popularity = {}
      }
    },
    sortPopular() {
      this.cancelBrowserLocation()
      this.setSort(SORT_POPULAR)
      this.nearRequested = false
      this.editingOrigin = false
    },
    // the position is only ever asked for from this tap, never on load
    sortNear() {
      this.setSort(SORT_NEAR)
      if (this.origin) {
        return
      }
      this.nearRequested = true
      this.requestBrowserLocation()
    },
    editOrigin() {
      this.postcodeError = null
      this.editingOrigin = true
    },
    requestBrowserLocation() {
      if (!this.hasGeolocation) {
        this.geoState = 'unavailable'
        this.editingOrigin = true
        return
      }
      const request = this.startBrowserLocation()
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (request !== this.geoRequest) return
          this.stopLocating()
          // a geocode still on its way must not take over from this position
          this.geocodeRequest++
          this.geocoding = false
          this.geoState = null
          this.browserPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
          this.originSource = 'browser'
          writeStorage(
            'sessionStorage',
            BROWSER_POSITION_KEY,
            JSON.stringify(this.browserPosition)
          )
          this.editingOrigin = false
          this.nearRequested = false
          this.setSort(SORT_NEAR)
        },
        (error) => {
          if (request !== this.geoRequest) return
          this.stopLocating()
          // 1 = PERMISSION_DENIED
          this.geoState = error && error.code === 1 ? 'denied' : 'failed'
          this.editingOrigin = true
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      )
    },
    startBrowserLocation() {
      this.cancelBrowserLocation()
      const request = ++this.geoRequest
      this.locating = true
      // no answer at all: show the postcode field, but keep listening, a
      // late grant still sorts from the position
      this.geoWatchdog = setTimeout(() => {
        if (request !== this.geoRequest || !this.locating) return
        this.stopLocating()
        this.geoState = 'failed'
        this.editingOrigin = true
      }, GEOLOCATION_WATCHDOG_MS)
      return request
    },
    stopLocating() {
      this.locating = false
      clearTimeout(this.geoWatchdog)
      this.geoWatchdog = null
    },
    // the user chose something else (Populair or a postcode) while waiting
    cancelBrowserLocation() {
      this.geoRequest++
      this.stopLocating()
    },
    usePostcode() {
      const query = this.postcodeDraft.trim()
      if (!query) return Promise.resolve(false)
      return this.geocodePostcode(query).then((found) => {
        if (found) {
          this.cancelBrowserLocation()
          writeStorage('localStorage', POSTCODE_KEY, query)
          this.editingOrigin = false
          this.nearRequested = false
          this.setSort(SORT_NEAR)
        }
        return found
      })
    },
    // the same geocoding as the registration, but without the store action
    // (LOCATION_ADDRESS_COORDINATES): that one writes state.location.status,
    // which is also the loading state of this list
    geocodePostcode(query) {
      const request = ++this.geocodeRequest
      this.geocoding = true
      this.postcodeError = null
      return Promise.resolve(googleSearchLongAndLatOnMap(query)).then(
        (result) => {
          if (request !== this.geocodeRequest) return false
          this.geocoding = false
          const coords = toLatLng(result && result.location)
          if (!coords) {
            this.postcodeError =
              'We kunnen deze postcode of plaats niet vinden.'
            return false
          }
          this.postcodeOrigin = { query, ...coords }
          this.originSource = 'postcode'
          writeStorage(
            'sessionStorage',
            POSTCODE_ORIGIN_KEY,
            JSON.stringify(this.postcodeOrigin)
          )
          return true
        }
      )
    }
  }
}
</script>

<style lang="scss" scoped>
// same width as the location page (max-w-6xl), so the edges line up
.overview {
  @apply max-w-6xl;
}

.city-select {
  flex: 1 1 0;
  min-width: 0;
}

.sport-chip {
  @apply flex-shrink-0 h-9 px-4 rounded-full border border-gray-300 bg-white text-sm text-gray-800 whitespace-nowrap;
}
.sport-chip-active {
  @apply border-2 border-green-600 bg-green-100 text-green-900 font-medium;
}

.sort-option {
  @apply h-9 px-3 rounded-full text-sm text-gray-800;
}
.sort-option-active {
  @apply bg-green-600 text-white font-medium;
}

.location-grid {
  @apply flex flex-col gap-3;
}
.location-card {
  @apply flex gap-3.5 p-2.5 border border-gray-200 rounded-xl bg-white;
  &:hover {
    @apply bg-gray-50;
  }
}
.location-card-photo {
  @apply flex-shrink-0 rounded-lg;
  width: 88px;
  height: 88px;
}
.location-card-body {
  gap: 3px;
}
.location-card-name {
  font-size: 15px;
}
.location-card-distance {
  font-size: 12px;
}
.location-card-meta,
.location-card-price {
  font-size: 13px;
}
.location-card-footer {
  @apply mt-auto flex flex-col items-start gap-0.5;
}
.card-label {
  @apply inline-block px-2 border border-green-600 rounded-full text-green-700 font-medium;
  height: 22px;
  line-height: 20px;
  font-size: 11px;
}

@media (max-width: 767px) {
  // mobile: the chips get their own row and scroll sideways edge to edge
  .sport-chips {
    order: 10;
    width: calc(100% + 40px);
    margin: 4px -20px 0;
    padding: 2px 20px;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}

@media (min-width: 768px) {
  .city-select {
    flex: 0 1 240px;
  }
  .sport-chips {
    flex-wrap: wrap;
  }
  .filters-spacer {
    flex: 1 1 0;
  }
  .location-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }
  .location-card {
    @apply flex-col gap-0 p-0 overflow-hidden;
  }
  .location-card-photo {
    @apply rounded-none;
    width: 100%;
    height: 168px;
  }
  .location-card-body {
    gap: 4px;
    padding: 14px 16px 16px;
  }
  .location-card-name {
    font-size: 16px;
  }
  .location-card-distance {
    font-size: 13px;
  }
  .location-card-meta,
  .location-card-price {
    font-size: 14px;
  }
  .location-card-footer {
    @apply mt-2 flex-row items-center justify-between gap-2;
  }
}
</style>
